import {
  Dialog,
  DialogHeader,
  DialogProps,
  Box,
  DialogContent,
  InputText,
  DialogFooter,
  Button,
  Text,
  Stack,
  InputGroup,
  FormLabel,
  InputMessage,
} from "@aksara-ui/react";
import { Field, FieldProps, Form, Formik, FormikValues } from "formik";
import React, { useState, useEffect, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { storeTier, updateTier } from "redux/actions";
import styled from "styled-components";
import * as yup from "yup";
import ImageDropzone from "components/ImageDropzone";
import { InputSelect } from "components/Form/InputSelect";
import { getRole } from "hooks";

const FormWrapper = styled(Box)`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const GridWrapper = styled(Box)`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 12px;
`;

interface Props {
  tierId: string;
}

interface ValueSchema {
  label: string;
  value: string | number;
}

const initialValues = {
  tierName: "",
  tierBackground: "",
  pointsLow: "",
  pointsHigh: "",
  maxCollectPointPerWeek: "",
  maxCollectPointPerMonth: "",
  minTransaction: "",
  tenantId: "",
};

const TierFormModal: React.FC<Props & DialogProps> = ({
  isOpen,
  onClose,
  tierId,
}) => {
  const isAddMode = !tierId;
  const dispatch = useDispatch();
  const userRole = getRole();
  const formikRef = useRef<FormikValues>() as any;

  const [tenant, setTenant] = useState<ValueSchema | null>(null);
  const [filesIcon, setFilesIcon] = useState<any>([]);
  const [filesCard, setFilesCard] = useState<any>([]);
  const [filesCardTemplate, setFilesCardTemplate] = useState<any>([]);

  const { appUrl } = useSelector((state: any) => state.globalReducer);
  const { isFormLoading } = useSelector((state: any) => state.tierReducer);
  const { tiers } = useSelector((state: any) => state.tierReducer);

  const [tenantItems] = React.useState([
   { label: "Ancol Points", value: 3 },
  ]);

  const handleCloseModal = () => {
    onClose && onClose();
    setTenant(null);
    setFilesIcon([]);
    setFilesCard([]);
    setFilesCardTemplate([]);
  };

  const handleSubmit = (values: any) => {
    const {
      tierName,
      tierBackground,
      pointsLow,
      pointsHigh,
      maxCollectPointPerWeek,
      maxCollectPointPerMonth,
      minTransaction,
      tenantId,
    } = values;
    const tierIcon = typeof filesIcon[0] === "object" ? filesIcon[0] : null;
    const tierCard = typeof filesCard[0] === "object" ? filesCard[0] : null;
    const tierCardTemplate =
      typeof filesCardTemplate[0] === "object" ? filesCardTemplate[0] : null;

    isAddMode
      ? dispatch(
          storeTier(
            {
              tierName,
              tierBackground,
              pointsLow,
              pointsHigh,
              maxCollectPointPerWeek,
              maxCollectPointPerMonth,
              minTransaction,
              tierIcon,
              tierCard,
              tierCardTemplate,
              tenantId,
            },
            handleCloseModal
          ) as any
        )
      : dispatch(
          updateTier(
            tierId,
            {
              tierName,
              tierBackground,
              pointsLow,
              pointsHigh,
              maxCollectPointPerWeek,
              maxCollectPointPerMonth,
              minTransaction,
              tierIcon,
              tierCard,
              tierCardTemplate,
              tenantId,
            },
            handleCloseModal
          ) as any
        );
  };

  useEffect(() => {
    if (!isAddMode) {
      const tierDetail = tiers.find(
        (item: any) => item.id === parseInt(tierId)
      );

      if (formikRef.current) {
        formikRef.current.setFieldValue("tierName", tierDetail.tier_name);
        formikRef.current.setFieldValue(
          "tierBackground",
          tierDetail.tier_background
        );
        formikRef.current.setFieldValue("pointsLow", tierDetail.points_low);
        formikRef.current.setFieldValue("pointsHigh", tierDetail.points_high);
        formikRef.current.setFieldValue(
          "maxCollectPointPerWeek",
          tierDetail.max_collect_point_per_week
        );
        formikRef.current.setFieldValue(
          "maxCollectPointPerMonth",
          tierDetail.max_collect_point_per_month
        );
        formikRef.current.setFieldValue(
          "minTransaction",
          tierDetail.transactions_low
        );
        formikRef.current.setFieldValue("tenantId", tierDetail.tenant_id);

        tenantItems.map((item) =>
          item.value.toString() === tierDetail.tenant_id
            ? setTenant(item)
            : null
        );

        setFilesIcon(
          tierDetail.tier_icon
            ? [appUrl + "/storage/" + tierDetail.tier_icon]
            : []
        );
        setFilesCard(
          tierDetail.tier_card
            ? [appUrl + "/storage/" + tierDetail.tier_card]
            : []
        );
        setFilesCardTemplate(
          tierDetail.tier_card_template
            ? [appUrl + "/storage/" + tierDetail.tier_card_template]
            : []
        );
      }
    }
  }, [isAddMode, tiers, tierId, appUrl]);

  const validationSchema = yup.object({
    tierName: yup.string().required("Tier Name is required field"),
    tierBackground: yup.string().required("Tier Background is required field"),
    pointsLow: yup.number().required("Points Low is required field"),
    pointsHigh: yup.number().required("Points High is required field"),
    maxCollectPointPerWeek: yup.number(),
    maxCollectPointPerMonth: yup.number(),
    minTransaction: yup.number(),
    tenantId:
      userRole === "superadmin"
        ? yup.string().required("Tenant is required field")
        : yup.string(),
  });

  return (
    <Dialog
      disableOverlayClick
      isOpen={isOpen}
      onClose={() => handleCloseModal()}
      overflowY={"auto"}
    >
      <Formik
        innerRef={formikRef}
        initialValues={initialValues}
        validationSchema={validationSchema}
        onSubmit={handleSubmit}
      >
        {({ setFieldValue }) => {
          return (
            <Form>
              <DialogHeader>
                <Text fontWeight={700}>
                  {isAddMode ? "Add Tier" : "Update Tier"}
                </Text>
              </DialogHeader>
              <DialogContent>
                <FormWrapper>
                  <Field name="tierName">
                    {({ field, meta }: FieldProps<string>) => (
                      <Stack spacing="xs">
                        <FormLabel htmlFor="tierName">Tier Name</FormLabel>
                        <InputGroup inputSize={40}>
                          <InputText
                            type="text"
                            size={40}
                            errors={!!meta.error && meta.touched}
                            {...field}
                          />
                        </InputGroup>
                        {!!meta.error && meta.touched && (
                          <InputMessage variant="error">
                            {meta.error}
                          </InputMessage>
                        )}
                      </Stack>
                    )}
                  </Field>
                  <GridWrapper>
                    <Field name="pointsLow">
                      {({ field, meta }: FieldProps<string>) => (
                        <Stack spacing="xs">
                          <FormLabel htmlFor="pointsLow">Points Low</FormLabel>
                          <InputGroup inputSize={40}>
                            <InputText
                              type="number"
                              size={40}
                              errors={!!meta.error && meta.touched}
                              {...field}
                            />
                          </InputGroup>
                          {!!meta.error && meta.touched && (
                            <InputMessage variant="error">
                              {meta.error}
                            </InputMessage>
                          )}
                        </Stack>
                      )}
                    </Field>
                    <Field name="pointsHigh">
                      {({ field, meta }: FieldProps<string>) => (
                        <Stack spacing="xs">
                          <FormLabel htmlFor="pointsHigh">
                            Points High
                          </FormLabel>
                          <InputGroup inputSize={40}>
                            <InputText
                              type="number"
                              size={40}
                              errors={!!meta.error && meta.touched}
                              {...field}
                            />
                          </InputGroup>
                          {!!meta.error && meta.touched && (
                            <InputMessage variant="error">
                              {meta.error}
                            </InputMessage>
                          )}
                        </Stack>
                      )}
                    </Field>
                  </GridWrapper>
                  <GridWrapper>
                    <Field name="maxCollectPointPerWeek">
                      {({ field, meta }: FieldProps<string>) => (
                        <Stack spacing="xs">
                          <FormLabel htmlFor="maxCollectPointPerWeek">
                            Max Collect Point (Week)
                          </FormLabel>
                          <InputGroup inputSize={40}>
                            <InputText
                              type="number"
                              size={40}
                              errors={!!meta.error && meta.touched}
                              {...field}
                            />
                          </InputGroup>
                          {!!meta.error && meta.touched && (
                            <InputMessage variant="error">
                              {meta.error}
                            </InputMessage>
                          )}
                        </Stack>
                      )}
                    </Field>
                    <Field name="maxCollectPointPerMonth">
                      {({ field, meta }: FieldProps<string>) => (
                        <Stack spacing="xs">
                          <FormLabel htmlFor="maxCollectPointPerMonth">
                            Max Collect Point (Month)
                          </FormLabel>
                          <InputGroup inputSize={40}>
                            <InputText
                              type="number"
                              size={40}
                              errors={!!meta.error && meta.touched}
                              {...field}
                            />
                          </InputGroup>
                          {!!meta.error && meta.touched && (
                            <InputMessage variant="error">
                              {meta.error}
                            </InputMessage>
                          )}
                        </Stack>
                      )}
                    </Field>
                  </GridWrapper>
                   <Field name="minTransaction">
                      {({ field, meta }: FieldProps<string>) => (
                        <Stack spacing="xs">
                          <FormLabel htmlFor="minTransaction">
                            Min Transaction
                          </FormLabel>
                          <InputGroup inputSize={40}>
                            <InputText
                              type="number"
                              size={40}
                              errors={!!meta.error && meta.touched}
                              {...field}
                            />
                          </InputGroup>
                          {!!meta.error && meta.touched && (
                            <InputMessage variant="error">
                              {meta.error}
                            </InputMessage>
                          )}
                        </Stack>
                      )}
                    </Field>

                  <Field name="tierBackground">
                    {({ field, meta }: FieldProps<string>) => (
                      <Stack spacing="xs">
                        <FormLabel htmlFor="tierBackground">
                          Background
                        </FormLabel>
                        <InputGroup inputSize={40}>
                          <InputText
                            type="text"
                            size={40}
                            errors={!!meta.error && meta.touched}
                            {...field}
                          />
                        </InputGroup>
                        {!!meta.error && meta.touched && (
                          <InputMessage variant="error">
                            {meta.error}
                          </InputMessage>
                        )}
                      </Stack>
                    )}
                  </Field>
                  {userRole === "superadmin" ? (
                    <Field name="tenantId">
                      {({ field, meta }: FieldProps<string>) => (
                        <Stack spacing="xs">
                          <FormLabel htmlFor="tenantId">Tenant</FormLabel>
                          <InputGroup inputSize={40}>
                            <InputSelect
                              selectedItem={tenant}
                              width={"100%"}
                              placeholder="Select Tenant"
                              handleSelectedItemChange={({ selectedItem }) => {
                                if (selectedItem) {
                                  setTenant(selectedItem);
                                  setFieldValue("tenantId", selectedItem.value);
                                }
                              }}
                              itemToString={(item) =>
                                item ? `${item.label}` : ""
                              }
                              itemValue={(item) =>
                                item ? `${item.value}` : ""
                              }
                              items={tenantItems}
                              size={"lg"}
                              borderRadius={"6px"}
                              errors={!!meta.error && meta.touched}
                            />
                          </InputGroup>
                          {!!meta.error && meta.touched && (
                            <InputMessage variant="error">
                              {meta.error}
                            </InputMessage>
                          )}
                        </Stack>
                      )}
                    </Field>
                  ) : (
                    <></>
                  )}
                  <Stack spacing="md">
                    <Stack spacing="xs">
                      <FormLabel>Icon</FormLabel>
                      <ImageDropzone
                        files={filesIcon}
                        setFiles={setFilesIcon}
                      />
                    </Stack>
                    <Stack spacing="xs">
                      <FormLabel>Card</FormLabel>
                      <ImageDropzone
                        files={filesCard}
                        setFiles={setFilesCard}
                      />
                    </Stack>
                    <Stack spacing="xs">
                      <FormLabel>Card Template</FormLabel>
                      <ImageDropzone
                        files={filesCardTemplate}
                        setFiles={setFilesCardTemplate}
                      />
                    </Stack>
                  </Stack>
                </FormWrapper>
              </DialogContent>
              <DialogFooter>
                <Stack
                  display="flex"
                  flexDirection="row"
                  spacing="xs"
                  flex={1}
                  style={{ gap: 15 }}
                >
                  <Button
                    block
                    variant="outline"
                    onClick={() => handleCloseModal()}
                  >
                    Cancel
                  </Button>
                  <Button
                    type="submit"
                    block
                    variant="primary"
                    isLoading={isFormLoading}
                  >
                    {isAddMode ? "Add" : "Update"}
                  </Button>
                </Stack>
              </DialogFooter>
            </Form>
          );
        }}
      </Formik>
    </Dialog>
  );
};

export default TierFormModal;
